import { ReactNode, Ref, useState } from 'react';
import { LoadErrorFooter, LoadingMoreFooter, NoMoreFooter } from './Footer';
import { Virtuoso, VirtuosoProps, VirtuosoHandle } from 'react-virtuoso';
import { useMemoizedFn, useMount } from 'ahooks';
import { Spin } from 'antd';

export interface LoadMoreV2Props<D, C> extends VirtuosoProps<D, C> {
  hasMore: boolean;
  loading?: boolean;
  loadingMore?: ReactNode;
  noMore?: ReactNode;
  loadError?: ReactNode;
  onLoad?: () => Promise<any>;
  onLoadMore: () => Promise<any>;
  listRef?: Ref<VirtuosoHandle>;
}

export function LoadMoreV2<D, C>(props: LoadMoreV2Props<D, C>) {
  const {
    hasMore,
    loading,
    loadingMore,
    noMore,
    loadError,
    onLoad,
    onLoadMore,
    listRef,
    ...virtuoso
  } = props;

  const [internalLoading, setInternalLoading] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const endReached = useMemoizedFn(() => {
    if (loadMoreLoading || internalLoading || !hasMore) return;

    setLoadMoreLoading(true);
    onLoadMore()
      .catch(() => {
        setHasError(true);
      })
      .finally(() => {
        setLoadMoreLoading(false);
      });
  });

  let Footer;
  if (!internalLoading || !loading) {
    if (hasError) {
      Footer = () => (
        <LoadErrorFooter
          onClick={() => {
            setHasError(false);
            endReached();
          }}
        />
      );
    } else if (hasMore && loadMoreLoading) {
      Footer = LoadingMoreFooter;
    } else {
      Footer = NoMoreFooter;
    }
  }

  useMount(() => {
    if (onLoad) {
      setInternalLoading(true);
      onLoad().finally(() => {
        setInternalLoading(false);
      });
    }
  });

  return (
    <div className="w-full h-full flex flex-col justify-center">
      {internalLoading || loading ? (
        <Spin />
      ) : (
        <Virtuoso
          ref={listRef}
          endReached={endReached}
          {...virtuoso}
          components={{
            Footer: virtuoso.data && virtuoso.data.length > 0 ? Footer : undefined,
            ...virtuoso.components,
          }}
        />
      )}
    </div>
  );
}
